import React, { useState, useEffect } from 'react';
import { sendPaystubNotifications, validatePhoneNumber } from '../utils/smsService';
import { supabase } from '../supabaseClient';

const TimesheetReviewSMSModal = ({ isOpen, onClose, employees, selectedPeriod }) => {
  const [sending, setSending] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [result, setResult] = useState(null);
  const [periodValidation, setPeriodValidation] = useState({
    isValid: false,
    isValidating: true,
    processingPeriods: [],
    errors: [],
    warnings: [],
    confirmedByUser: false
  });

  // Validate period for timesheet review SMS
  const validatePeriodForSMS = async () => {
    console.log('Starting period validation for timesheet SMS...');
    
    try {
      setPeriodValidation(prev => ({ ...prev, isValidating: true }));
      
      // Query all periods with Processing status
      const { data: processingPeriods, error: queryError } = await supabase
        .from('payroll_period')
        .select('*')
        .eq('status', 'Processing')
        .order('payroll_number', { ascending: false });
      
      if (queryError) {
        console.error('Error querying processing periods:', queryError);
        setPeriodValidation({
          isValid: false,
          isValidating: false,
          processingPeriods: [],
          errors: [`Database error: ${queryError.message}`],
          warnings: [],
          confirmedByUser: false
        });
        return;
      }
      
      console.log('Processing periods found:', processingPeriods);
      
      const errors = [];
      const warnings = [];
      
      // Check 1: Exactly ONE period should be in Processing status
      if (processingPeriods.length === 0) {
        errors.push('No payroll period found in "Processing" status. Please set a period to Processing status before sending timesheet review SMS.');
      } else if (processingPeriods.length > 1) {
        errors.push(`CRITICAL: ${processingPeriods.length} periods are in "Processing" status. Only ONE period should be in Processing status at a time!`);
        errors.push(`Please fix this immediately by updating period statuses in Period Management.`);
      }
      
      // Check 2: If exactly one Processing period, validate it's within the last 2 weeks
      let validPeriod = null;
      if (processingPeriods.length === 1) {
        validPeriod = processingPeriods[0];
        
        const today = new Date();
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        const periodStartDate = new Date(validPeriod.start_date);
        const periodEndDate = new Date(validPeriod.end_date);
        
        console.log('Period date validation:', {
          periodStart: periodStartDate,
          periodEnd: periodEndDate,
          twoWeeksAgo,
          today
        });
        
        // Check if period end date is within the last 2 weeks or is current/future
        if (periodEndDate < twoWeeksAgo) {
          warnings.push(`The Processing period ended more than 2 weeks ago (${validPeriod.end_date}). This may be an old period.`);
        }
        
        // Check if the selected period matches the Processing period
        if (selectedPeriod && selectedPeriod.payroll_number !== validPeriod.payroll_number) {
          warnings.push(`Selected period (${selectedPeriod.label}) does NOT match the Processing period (${validPeriod.period_label}).`);
          warnings.push(`SMS will be sent for the Processing period: ${validPeriod.period_label}`);
        }
      }
      
      const isValid = errors.length === 0 && processingPeriods.length === 1;
      
      setPeriodValidation({
        isValid,
        isValidating: false,
        processingPeriods,
        errors,
        warnings,
        confirmedByUser: false,
        validPeriod
      });
      
      console.log('Validation complete:', { isValid, errors, warnings });
      
    } catch (error) {
      console.error('Unexpected error during period validation:', error);
      setPeriodValidation({
        isValid: false,
        isValidating: false,
        processingPeriods: [],
        errors: [`Unexpected error: ${error.message}`],
        warnings: [],
        confirmedByUser: false
      });
    }
  };
  
  // Run validation when modal opens
  useEffect(() => {
    if (isOpen) {
      validatePeriodForSMS();
    } else {
      // Reset validation when modal closes
      setPeriodValidation({
        isValid: false,
        isValidating: true,
        processingPeriods: [],
        errors: [],
        warnings: [],
        confirmedByUser: false
      });
    }
  }, [isOpen, selectedPeriod]);

  // Get timesheet review message template
  const getTimesheetReviewMessage = (employeeName = null) => {
    const greeting = employeeName ? `Hi ${employeeName.split(' ')[0]}!` : "Hi!";
    
    // Use the validated Processing period if available, otherwise fall back to selectedPeriod
    const period = periodValidation.validPeriod || selectedPeriod;
    const periodText = period ? 
      `for the period ${period.start_date || period.start} to ${period.end_date || period.end}` : 
      'for the current period';
    
    return `${greeting} Your timesheet ${periodText} is ready for review. Please check your hours at https://pay.starside.app and submit any questions by Tuesday. Payroll processes Wednesday, pays Friday. Thank you!`;
  };

  const defaultMessage = getTimesheetReviewMessage();

  const handleSendSMS = async () => {
    setSending(true);
    setResult(null);

    try {
      // Create personalized messages for each recipient
      const recipientsWithMessages = employees.map(emp => {
        const personalizedMessage = customMessage.trim() || getTimesheetReviewMessage(emp.employee_name);
        
        return {
          employee_name: emp.employee_name,
          employee_number: emp.employee_number,
          phone: emp.phone,
          message: personalizedMessage
        };
      });

      // Validate phone numbers
      const validRecipients = recipientsWithMessages.filter(emp => {
        return emp.phone && validatePhoneNumber(emp.phone);
      });
      
      if (validRecipients.length === 0) {
        setResult({
          success: false,
          message: 'No valid phone numbers found for selected employees.'
        });
        setSending(false);
        return;
      }
      
      // Call the SMS API with payroll number for validation
      const apiResult = await sendPaystubNotifications(validRecipients, 'sms', selectedPeriod?.payroll_number);
      
      setResult({
        success: apiResult.success,
        sent: apiResult.sent || 0,
        failed: apiResult.failed || 0,
        message: apiResult.message || 'Timesheet review notifications processed',
        details: apiResult.results || []
      });

    } catch (error) {
      console.error('Error sending timesheet review SMS:', error);
      setResult({
        success: false,
        message: `Failed to send timesheet review notifications: ${error.message}`
      });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setCustomMessage('');
    setPeriodValidation({
      isValid: false,
      isValidating: true,
      processingPeriods: [],
      errors: [],
      warnings: [],
      confirmedByUser: false
    });
    onClose();
  };

  if (!isOpen) return null;

  const validEmployees = employees.filter(emp => emp.phone && validatePhoneNumber(emp.phone));
  const invalidEmployees = employees.filter(emp => !emp.phone || !validatePhoneNumber(emp.phone));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Send Timesheet Review SMS</h2>
          <button
            onClick={handleClose}
            disabled={sending}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Period Validation Status */}
        {periodValidation.isValidating && (
          <div className="mb-4 p-4 bg-gray-50 border border-gray-300 rounded">
            <div className="flex items-center">
              <svg className="w-5 h-5 animate-spin text-gray-600 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm text-gray-700 font-medium">Validating payroll period...</p>
            </div>
          </div>
        )}

        {/* Validation Errors */}
        {!periodValidation.isValidating && periodValidation.errors.length > 0 && (
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-500 rounded">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-red-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-bold text-red-800 mb-2">VALIDATION FAILED - Cannot Send SMS</p>
                <ul className="list-disc list-inside space-y-1">
                  {periodValidation.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-700">{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Validation Warnings */}
        {!periodValidation.isValidating && periodValidation.warnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-500 rounded">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-yellow-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-bold text-yellow-800 mb-2">WARNING - Please Review</p>
                <ul className="list-disc list-inside space-y-1">
                  {periodValidation.warnings.map((warning, index) => (
                    <li key={index} className="text-sm text-yellow-700">{warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Valid Period Info */}
        {!periodValidation.isValidating && periodValidation.isValid && periodValidation.validPeriod && (
          <div className="mb-4 p-4 bg-green-50 border-2 border-green-500 rounded">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-bold text-green-800 mb-1">Period Validated</p>
                <p className="text-sm text-green-700">
                  <strong>Processing Period:</strong> {periodValidation.validPeriod.period_label}
                </p>
                <p className="text-sm text-green-600">
                  {periodValidation.validPeriod.start_date} to {periodValidation.validPeriod.end_date}
                  {periodValidation.validPeriod.pay_date && ` • Pay Date: ${periodValidation.validPeriod.pay_date}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Checkbox */}
        {!periodValidation.isValidating && periodValidation.isValid && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-300 rounded">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={periodValidation.confirmedByUser}
                onChange={(e) => setPeriodValidation(prev => ({ ...prev, confirmedByUser: e.target.checked }))}
                className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-blue-900">
                <strong>I confirm</strong> that the period <strong>{periodValidation.validPeriod?.period_label}</strong> is the correct period for sending timesheet review SMS notifications to staff.
              </span>
            </label>
          </div>
        )}

        {/* Recipients Summary */}
        <div className="mb-4">
          <h3 className="font-medium text-gray-800 mb-2">Recipients Summary</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-green-50 p-3 rounded">
              <p className="font-medium text-green-800">Valid Recipients</p>
              <p className="text-green-600">{validEmployees.length} employees with phone numbers</p>
            </div>
            <div className="bg-yellow-50 p-3 rounded">
              <p className="font-medium text-yellow-800">Missing Phone Numbers</p>
              <p className="text-yellow-600">{invalidEmployees.length} employees</p>
            </div>
          </div>
        </div>

        {/* Valid Recipients List */}
        {validEmployees.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Will send to ({validEmployees.length}):
            </h4>
            <div className="max-h-32 overflow-y-auto bg-gray-50 p-2 rounded text-xs">
              {validEmployees.map((emp, index) => (
                <div key={index} className="flex justify-between py-1">
                  <span>{emp.employee_name}</span>
                  <span className="text-gray-500">{emp.phone}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invalid Recipients List */}
        {invalidEmployees.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-yellow-700 mb-2">
              Missing phone numbers ({invalidEmployees.length}):
            </h4>
            <div className="max-h-24 overflow-y-auto bg-yellow-50 p-2 rounded text-xs">
              {invalidEmployees.map((emp, index) => (
                <div key={index} className="py-1 text-yellow-800">
                  {emp.employee_name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Message Editor */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message (Leave blank to use default)
          </label>
          <textarea
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            disabled={sending}
            rows={4}
            className="w-full p-3 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-sm"
            placeholder={defaultMessage}
          />
          <p className="text-xs text-gray-500 mt-1">
            Character count: {(customMessage || defaultMessage).length}/160
          </p>
        </div>

        {/* Default Message Preview */}
        {!customMessage && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
            <p className="text-xs text-gray-600 mb-1">Default message preview:</p>
            <p className="text-sm text-gray-800">{defaultMessage}</p>
          </div>
        )}

        {/* Result Display */}
        {result && (
          <div className={`mb-4 p-3 rounded ${
            result.success 
              ? 'bg-green-100 border border-green-300' 
              : 'bg-red-100 border border-red-300'
          }`}>
            <p className={`text-sm font-medium ${
              result.success ? 'text-green-800' : 'text-red-800'
            }`}>
              {result.success ? 'Success!' : 'Error'}
            </p>
            <p className={`text-sm ${
              result.success ? 'text-green-700' : 'text-red-700'
            }`}>
              {result.message}
            </p>
            {result.success && result.sent > 0 && (
              <p className="text-xs text-green-600 mt-1">
                Sent: {result.sent}, Failed: {result.failed}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={sending}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleSendSMS}
            disabled={
              validEmployees.length === 0 || 
              sending || 
              periodValidation.isValidating ||
              !periodValidation.isValid ||
              !periodValidation.confirmedByUser
            }
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            title={
              !periodValidation.isValid 
                ? 'Period validation failed - please fix errors before sending' 
                : !periodValidation.confirmedByUser 
                ? 'Please confirm the correct period before sending'
                : ''
            }
          >
            {sending && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {sending ? 'Sending...' : `Send to ${validEmployees.length} employees`}
          </button>
        </div>

        {/* Instructions */}
        <div className="mt-4 text-xs text-gray-500 border-t pt-3">
          <p><strong>Timesheet Review Instructions:</strong></p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Employees will receive SMS notification about timesheet review</li>
            <li>They have until Tuesday to report any working hour errors</li>
            <li>Payroll will be processed based on current attendance records</li>
            <li>Only employees with valid phone numbers will receive notifications</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TimesheetReviewSMSModal;